import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { StatusBadge, usePaginated, Paginator } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CopyVin } from "@/components/CopyVin";
import { LinkedCustomers } from "@/components/LinkedCustomers";
import { ArrowLeft, Building2, Car, Wrench, FileText, Mail, Phone, MapPin, Loader2, Gauge, CalendarClock, ChevronRight, User } from "lucide-react";

const DOC_LABELS = { ostra: "Faktúra", ponuka: "Ponuka", zalohova: "Zálohová" };
const ROW = "group flex w-full cursor-pointer flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background/40 p-3 text-left transition-colors hover:border-primary/60 hover:bg-accent/40";
const Arrow = () => <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />;

function PagedList({ list, testid, empty, render }) {
  const pg = usePaginated(list, 10);
  return (
    <div className="space-y-2" data-testid={testid}>
      {pg.paged.map(render)}
      {!list.length && <p className="py-4 text-center text-sm text-muted-foreground">{empty}</p>}
      {list.length > 10 && <div data-testid={`${testid}-pager`}><Paginator {...pg} /></div>}
    </div>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <div className="card-surface p-5">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-wide">{title}</h2>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-muted-foreground">{count}</span>
      </div>
      {children}
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/${id}/detail`);
      setData(res.data);
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!data) return <div className="py-24 text-center text-muted-foreground">Zákazník sa nenašiel.</div>;

  const { customer: c, vehicles, jobs, invoices, total_invoiced, linked = [], company_totals = [] } = data;

  return (
    <div data-testid="customer-detail-page">
      <Button variant="ghost" className="mb-4 gap-2" onClick={() => navigate("/zakaznici")} data-testid="customer-detail-back">
        <ArrowLeft className="h-4 w-4" /> Späť na zákazníkov
      </Button>

      {/* Header card */}
      <div className="card-surface mb-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/15">{c.is_company === false ? <User className="h-6 w-6 text-primary" /> : <Building2 className="h-6 w-6 text-primary" />}</div>
              <div>
                <h1 className="font-heading text-2xl font-extrabold uppercase tracking-wide sm:text-3xl">{c.name}</h1>
                <span className="rounded bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase">{c.country}</span>
              </div>
            </div>
            <div className="mt-4 grid gap-1.5 text-sm text-muted-foreground sm:grid-cols-2 sm:gap-x-8">
              {c.ico && <div>IČO: <span className="font-mono text-foreground">{c.ico}</span></div>}
              {c.dic && <div>DIČ: <span className="font-mono text-foreground">{c.dic}</span></div>}
              {c.ic_dph && <div>IČ DPH: <span className="font-mono text-foreground">{c.ic_dph}</span></div>}
              {(c.address || c.city) && <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{[c.address, c.psc, c.city].filter(Boolean).join(", ")}</div>}
              {c.email && <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{c.email}</div>}
              {c.phone && <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{c.phone}</div>}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-background/50 px-5 py-4 text-right">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Fakturované (ostré)</div>
            <div className="font-heading text-2xl font-bold text-primary" data-testid="customer-total-invoiced">{eur(total_invoiced)}</div>
            {company_totals.map((t) => <div key={t.id} className="mt-1 text-xs text-muted-foreground" data-testid={`customer-company-total-${t.id}`}>{t.name}: <b className="font-mono text-foreground">{eur(t.total)}</b></div>)}
          </div>
        </div>
      </div>

      <LinkedCustomers customer={c} linked={linked} onChanged={load} />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Vehicles */}
        <Section icon={Car} title="Vozidlá" count={vehicles.length}>
          <PagedList list={vehicles} testid="detail-vehicles" empty="Žiadne vozidlá." render={(v) => (
              <button type="button" key={v.id} onClick={() => navigate(`/vozidla?open=${v.id}`)} className={ROW} data-testid={`detail-vehicle-${v.id}`}>
                <div className="flex items-center gap-3">
                  <span className="plate">{v.spz}</span>
                  <div>
                    <div className="font-semibold">{v.brand} {v.model}{v.year ? ` · ${v.year}` : ""}{v.via_person && <span className="ml-2 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-300">cez osobu</span>}</div>
                    <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                      {v.km != null && <span className="flex items-center gap-1"><Gauge className="h-3 w-3" />{v.km.toLocaleString("sk-SK")} km</span>}
                      {v.vin && <CopyVin vin={v.vin} testid={`detail-vehicle-vin-${v.id}`} />}
                      {v.stk_valid_until && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />STK {v.stk_valid_until}</span>}
                    </div>
                  </div>
                </div>
                <Arrow />
              </button>
          )} />
        </Section>

        {/* Jobs */}
        <Section icon={Wrench} title="Zákazky" count={jobs.length}>
          <PagedList list={jobs} testid="detail-jobs" empty="Žiadne zákazky." render={(j) => {
              const total = (j.items || []).reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * (1 + Number(it.vat_rate || 0) / 100), 0);
              return (
                <button type="button" key={j.id} onClick={() => navigate(`/zakazky/${j.id}/upravit`)} className={ROW} data-testid={`detail-job-${j.id}`}>
                  <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{j.title}</span>
                      <StatusBadge status={j.status} />
                    </div>
                    <span className="font-mono text-sm text-primary">{eur(total)}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    {j.vehicle?.spz && <span className="plate">{j.vehicle.spz}</span>}
                    {j.vehicle && <span>{j.vehicle.brand} {j.vehicle.model}</span>}
                    <span>{(j.created_at || "").slice(0, 10)}</span>
                  </div>
                  </div>
                  <Arrow />
                </button>
              );
            }} />
        </Section>

        {/* Invoices */}
        <div className="lg:col-span-2">
          <Section icon={FileText} title="Faktúry a doklady" count={invoices.length}>
            <PagedList list={invoices} testid="detail-invoices" empty="Žiadne faktúry." render={(inv) => (
                <button type="button" key={inv.id} onClick={() => navigate(`/faktury?preview=${inv.id}`)} className={ROW} data-testid={`detail-invoice-${inv.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="rounded bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase">{DOC_LABELS[inv.doc_type] || inv.doc_type}</span>
                    <span className="font-heading font-bold">{inv.number}</span>
                    <span className="text-sm text-muted-foreground">{inv.issue_date}</span>
                    {inv.status === "paid" && <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-400">Zaplatené</span>}
                    {inv.billed_to && <span className="rounded bg-sky-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-300" data-testid={`detail-invoice-billed-${inv.id}`}>firma: {inv.billed_to}</span>}
                  </div>
                  <span className="flex items-center gap-2 font-mono font-bold text-primary">{eur(inv.total)}<Arrow /></span>
                </button>
            )} />
          </Section>
        </div>
      </div>
    </div>
  );
}
